import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import { changeOwnPassword } from "@/lib/admin-api";
import { validatePassword, PASSWORD_POLICY } from "@/lib/password-policy";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/_authenticated/change-password")({
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const { mustChangePassword, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const localError = pw1 ? validatePassword(pw1) : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (localError) return toast.error(localError);
    if (pw1 !== pw2) return toast.error("Les deux mots de passe ne correspondent pas.");
    setBusy(true);
    try {
      await changeOwnPassword({ new_password: pw1 });
      toast.success("Mot de passe mis à jour.");
      await refreshProfile();
      navigate({ to: "/" });
    } catch (err: any) {
      toast.error(err.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={logo} alt="LocMat" className="mx-auto h-12 w-auto" />
            <CardTitle className="mt-2 inline-flex items-center justify-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Définir votre mot de passe
            </CardTitle>
            {mustChangePassword && (
              <CardDescription>
                Pour sécuriser votre compte, vous devez choisir un mot de passe personnel avant de continuer.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="pw1">Nouveau mot de passe</Label>
                <Input id="pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw2">Confirmation</Label>
                <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-5">
                <li>Au moins {PASSWORD_POLICY.minLength} caractères</li>
                <li>Au moins une majuscule, une minuscule, un chiffre</li>
                <li>Au moins un caractère spécial</li>
              </ul>
              {localError && <p className="text-xs text-destructive">{localError}</p>}
              <Button type="submit" className="w-full" disabled={busy || !!localError || !pw1 || pw1 !== pw2}>
                {busy ? "…" : "Enregistrer"}
              </Button>
              {mustChangePassword && (
                <Button type="button" variant="ghost" className="w-full" onClick={signOut}>
                  Se déconnecter
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
